import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AuthLayout, AuthAlert } from './AuthLayout';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { OtpInput } from './OtpInput';
import { useResendCooldown } from './useResendCooldown';
import { verifyRecoveryOtp, updatePassword, resendRecoveryCode } from './api';

export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = params.get('email') ?? '';
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const cooldown = useResendCooldown();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPasswordError(null);
    if (code.length !== 8) {
      setError(t('resetPassword.codeRequired'));
      return;
    }
    if (password.length < 10) {
      setPasswordError(t('resetPassword.passwordTooShort'));
      return;
    }
    setSubmitting(true);
    try {
      await verifyRecoveryOtp(email, code);
      await updatePassword(password);
      navigate('/', { replace: true });
    } catch {
      setError(t('resetPassword.verifyError'));
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!cooldown.canResend || !email) return;
    try {
      await resendRecoveryCode(email);
      cooldown.start();
    } catch {
      setError(t('otp.resendError'));
    }
  }

  if (!email) {
    return (
      <AuthLayout title={t('resetPassword.title')}>
        <AuthAlert>
          {t('resetPassword.startPrefix')}
          <Link to="/forgot-password" className="underline">{t('resetPassword.forgotPasswordLink')}</Link>
          {t('resetPassword.startSuffix')}
        </AuthAlert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('resetPassword.codeTitle')} subtitle={t('resetPassword.codeSubtitle', { email })}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {error ? <AuthAlert>{error}</AuthAlert> : null}
        <OtpInput value={code} onChange={setCode} />
        <button
          type="button"
          onClick={handleResend}
          disabled={!cooldown.canResend}
          className="text-left text-sm font-semibold text-accent-ink disabled:text-muted disabled:no-underline hover:underline"
        >
          {cooldown.canResend ? t('otp.resendCode') : t('otp.resendCodeIn', { seconds: cooldown.secondsLeft })}
        </button>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">{t('resetPassword.newPasswordLabel')}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <FieldError>{passwordError}</FieldError>
        </div>
        <Button type="submit" loading={submitting} className="mt-2 w-full">
          {t('resetPassword.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
